import { useState } from 'react'

import { api } from '../services/api.service'

import { PhoneField, TextAreaField } from './fields'

export default function FindProductForm(props) {
    const [values, setValues] = useState({})
    const [error, setError] = useState('')
    const [shareLocation, setShareLocation] = useState(null) // null = not selected, true = Yes, false = No
    const [locationStatus, setLocationStatus] = useState('')
    const [gettingLocation, setGettingLocation] = useState(false)
    const [locationAttempts, setLocationAttempts] = useState(0)

    // Check if GPS sharing is allowed (passed from parent component)
    const isGPSAllowed = props.allowFinderGPS !== false

    const getLocation = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by your browser'))
                return
            }

            setGettingLocation(true)
            setLocationStatus('Getting your location...')

            // iOS-specific: Try with low accuracy first as fallback strategy
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
            const attempt = locationAttempts

            // First attempt: high accuracy with longer timeout for iOS
            // Second attempt (if first fails): lower accuracy, faster timeout
            const options =
                attempt === 0
                    ? {
                          enableHighAccuracy: true,
                          timeout: isIOS ? 20000 : 10000, // iOS needs more time
                          maximumAge: 0,
                      }
                    : {
                          enableHighAccuracy: false,
                          timeout: 8000,
                          maximumAge: 30000, // Accept cached location on retry
                      }

            const timeoutId = setTimeout(() => {
                setGettingLocation(false)
                setLocationStatus('')
                reject(new Error('Location request timed out'))
            }, options.timeout + 1000)

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    clearTimeout(timeoutId)
                    setGettingLocation(false)
                    setLocationStatus('Location captured successfully!')
                    setLocationAttempts(0)
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                    })
                },
                (error) => {
                    clearTimeout(timeoutId)
                    setGettingLocation(false)
                    setLocationStatus('')

                    // iOS-specific error handling
                    let errorMessage = 'Could not get location'
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Location permission denied. Please enable location access in your device settings.'
                            break
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Location information unavailable. Please ensure location services are enabled.'
                            break
                        case error.TIMEOUT:
                            errorMessage = 'Location request timed out. Please try again.'
                            break
                    }

                    reject(new Error(errorMessage))
                },
                options
            )
        })
    }

    const handleSubmit = async () => {
        if (!values.note) {
            setError("Whoops! Make sure you leave a message for the owner of this item so they know it's in safe hands and all is not lost.")
            return
        }
        if (!values.phoneNumber) {
            setError("Whoops! Make sure you leave a number so you can receive a text from Boomerang once the owner's return details are available. It will never be shared or spammed.")
            return
        }

        let dataToSend = { ...values }

        // Get location if user opted to share it (selected "Yes")
        if (shareLocation === true) {
            try {
                const location = await getLocation()
                dataToSend.location = location
            } catch (error) {
                console.error('Error getting location:', error)

                // On iOS, offer retry with lower accuracy
                if (locationAttempts === 0 && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
                    setLocationAttempts(1)
                    setError("Having trouble getting precise location. Click 'Send' again to try with approximate location, or select 'No' to proceed without location.")
                    return
                }

                // After retry attempt, offer to continue without location
                const userMessage = error.message || 'Could not get your location.'
                setError(`${userMessage} You can select 'No' to proceed without sharing location, or check your device's location settings and try again.`)
                setLocationAttempts(0)
                return
            }
        }

        // Reset attempts on successful submission
        setLocationAttempts(0)

        await api(`${process.env.REACT_APP_API_URL}/public/find-product/${props.id}`, dataToSend)
        props.submitted(values.phoneNumber)
    }

    return (
        <div className='max-h-[70vh] overflow-y-auto overflow-x-hidden px-1'>
            <h2 className='text-base sm:text-lg font-semibold mb-2'>Thanks for helping return this item!</h2>

            <div className='mb-3'>
                <div className='text-sm font-medium mb-1.5'>1. Send the owner a message via Boomerang.</div>
                <TextAreaField placeholder="Let the owner know you've found it and where it is, or the best way to contact you to get it back." labelClass='text-black' value={values.note} onChange={(val) => setValues({ ...values, note: val })} />
            </div>

            <div className='mb-3'>
                <div className='text-sm font-medium mb-1.5'>2. Share your number with Boomerang. We won't share your number with the owner but we will send you a text if there are any updates about the item.</div>
                <PhoneField defaultCountry={'uk'} displayInitialValueAsLocalNumber value={values.phoneNumber} onChange={(val) => setValues({ ...values, phoneNumber: val })} />
            </div>

            {isGPSAllowed && (
                <div className='mb-3 flex flex-col'>
                    <div className='text-sm font-medium mb-1.5'>3. Share the exact GPS location with the owner? (Optional)</div>
                    <div className='text-[10px] sm:text-xs text-neutral-600 mb-1.5'>Note: Location services may require permission on your device. If you have issues, you can skip this step.</div>
                    <div className='flex mx-auto gap-6 mb-1.5'>
                        <label className='flex items-center gap-1.5 cursor-pointer'>
                            <div
                                className='w-5 h-5 border-2 border-black flex items-center justify-center cursor-pointer'
                                onClick={() => {
                                    setShareLocation(true)
                                    setError('')
                                    setLocationAttempts(0)
                                }}
                            >
                                {shareLocation === true && <div className='w-2.5 h-2.5 bg-black'></div>}
                            </div>
                            <span className='text-sm'>Yes</span>
                        </label>
                        <label className='flex items-center gap-1.5 cursor-pointer'>
                            <div
                                className='w-5 h-5 border-2 border-black flex items-center justify-center cursor-pointer'
                                onClick={() => {
                                    setShareLocation(false)
                                    setError('')
                                    setLocationStatus('')
                                    setLocationAttempts(0)
                                }}
                            >
                                {shareLocation === false && <div className='w-2.5 h-2.5 bg-black'></div>}
                            </div>
                            <span className='text-sm'>No</span>
                        </label>
                    </div>
                    {locationStatus && <div className='text-[10px] sm:text-xs text-green-600 mb-1.5'>{locationStatus}</div>}
                </div>
            )}

            <div className='text-center text-sm font-semibold mb-3'>They will be so relieved, thank you for returning this!</div>

            <div className='my-1.5 text-xs sm:text-sm text-red-500'>{error}</div>
            <button className='w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded text-sm' onClick={handleSubmit} type='button' disabled={gettingLocation}>
                {gettingLocation ? 'Getting location...' : 'Send'}
            </button>
        </div>
    )
}
